import * as React from "react"
import { useLocation } from "react-router-dom"
import {
  Gauge,
  Luggage,
  Hotel,
  MessageSquare,
  Award,
} from "lucide-react"
import { useAuth } from "@/features/auth/stores/AuthContext"

import { NavMain } from "@/features/admin/components/common/navigation/nav-main"
import { NavUser } from "@/features/admin/components/common/navigation/nav-user"
import { TeamSwitcher } from "@/features/admin/components/common/layout/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/shared/components/ui/sidebar"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation()
  const { user, hasPermission } = useAuth()
  
  // Admin navigation data with real user info
  const data = {
    user: {
      name: user?.name || "Admin User",
      email: user?.email || "admin@example.com",
      avatar: user?.avatar || "/avatars/admin.jpg",
    },
    teams: [
      {
        name: "Travel Warehouse",
        logo: "/logo.svg",
        plan: "Admin Portal",
      },
    ],
    navMain: [
      {
        title: "Dashboard",
        url: "/admin",
        icon: Gauge,
        isActive: true,
      },
      // Only show packages if user has read permission
      ...(hasPermission('packages:read') ? [{
        title: "Packages",
        url: "/admin/packages",
        icon: Luggage,
        items: [
          {
            title: "All Packages",
            url: "/admin/packages",
          },
          {
            title: "Archived",
            url: "/admin/packages/archived",
          },
          {
            title: "Expired",
            url: "/admin/packages/expired",
          },
          {
            title: "Deleted",
            url: "/admin/packages/deleted",
          },
        ],
      }] : []),
      // Testimonials - everyone can access
      {
        title: "Testimonials",
        url: "/admin/testimonials",
        icon: Award,
      },
      // Inbox with permission-based items
      {
        title: "Inbox",
        url: "/admin/inbox",
        icon: MessageSquare,
        items: [
          {
            title: "Messages",
            url: "/admin/inbox/messages",
          },
          {
            title: "Deleted Messages",
            url: "/admin/inbox/deleted-messages",
          },
          ...(hasPermission('visa:read') ? [{
            title: "Visa Applications",
            url: "/admin/inbox/visa-applications",
          }] : []),
          {
            title: "Travel Insurance",
            url: "/admin/inbox/travel-insurance",
          },
        ],
      },
      // Company Info - settings permission required
      ...(hasPermission('settings:read') ? [{
        title: "Company Info",
        url: "/admin/company-info",
        icon: Hotel,
      }] : []),
      // Legal Documents removed: pages are now static on the site
    ],
  }
  
  // Update navigation items with active state based on current location
  const navMainWithActive = data.navMain.map((item) => {
    const isActive = location.pathname === item.url || 
                     (item.items && item.items.some(subItem => location.pathname === subItem.url))
    const itemsWithActive = item.items?.map(subItem => ({
      ...subItem,
      isActive: location.pathname === subItem.url
    }))
    return {
      ...item,
      isActive,
      items: itemsWithActive
    }
  })

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMainWithActive} />
      </SidebarContent>
      <SidebarFooter>
        <div className="px-2 py-2">
          <NavUser />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
